import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, Field, Modal, MoneyInput, QueryStatus, useToast } from "@cx/ui";
import type { PaymentTermInput, PaymentTermsSettings, PaymentTermView } from "@cx/contracts";
import {
  buildInstallments,
  describeTerm,
  formatBRL,
  formatDateBR,
  isIsoDate,
  parseTermDays,
  termProblem,
  todayInSaoPaulo,
} from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";

type Editing = { id?: string; values: PaymentTermInput };
const EMPTY: PaymentTermInput = { name: "", days: [30], downPayment: false, active: true };

/** Condições de pagamento: prazos (30/60/90), entrada à vista e a prorrogação para dia útil (05 §4). */
export function PaymentTermsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const key = ["payment-terms", companyId];
  const list = useQuery({ queryKey: key, queryFn: () => api<{ data: PaymentTermView[] }>("/payment-terms") });
  const [editing, setEditing] = useState<Editing | null>(null);
  const [deleting, setDeleting] = useState<PaymentTermView | null>(null);
  const changed = () => qc.invalidateQueries({ queryKey: key });
  const del = useMutation({
    mutationFn: (id: string) => api(`/payment-terms/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("success", "Condição de pagamento excluída");
      changed();
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <>
      <ContentHeader
        title="Condições de pagamento"
        breadcrumb={["Cadastros", "Condições de pagamento"]}
        actions={
          can(perms, "payment_terms", "create") && (
            <button type="button" className="btn btn-primary" onClick={() => setEditing({ values: EMPTY })}>
              Incluir condição
            </button>
          )
        }
      />
      <div className="box card mb-3">
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && list.data.data.length === 0 && (
          <p className="text-muted p-3 mb-0">Nenhuma condição de pagamento cadastrada.</p>
        )}
        {list.isSuccess && list.data.data.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Condições de pagamento">
              <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">Prazos</th>
                  <th scope="col" className="text-end">
                    Parcelas
                  </th>
                  <th scope="col">Situação</th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.data.data.map((t) => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td>{describeTerm(t)}</td>
                    <td className="text-end">{t.days.length + (t.downPayment ? 1 : 0)}</td>
                    <td>{t.active ? "Ativa" : "Inativa"}</td>
                    <td className="text-end text-nowrap">
                      {can(perms, "payment_terms", "update") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-2"
                          aria-label={`Editar ${t.name}`}
                          onClick={() => setEditing({ id: t.id, values: { ...t } })}
                        >
                          Editar
                        </button>
                      )}
                      {can(perms, "payment_terms", "delete") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          aria-label={`Excluir ${t.name}`}
                          disabled={del.isPending}
                          onClick={() => setDeleting(t)}
                        >
                          Excluir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <SettingsBox />
      {editing && (
        <TermDialog
          id={editing.id}
          initial={editing.values}
          onClose={() => setEditing(null)}
          onSaved={changed}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Excluir condição de pagamento"
        message={`Excluir a condição "${deleting?.name ?? ""}"? Ela pode ser restaurada pela Consulta de exclusões.`}
        confirmLabel="Excluir"
        onConfirm={() => {
          if (deleting) del.mutate(deleting.id);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}

function useTermsSettings() {
  const { companyId, api } = useSession();
  return useQuery({
    queryKey: ["payment-terms", companyId, "settings"],
    queryFn: () => api<PaymentTermsSettings>("/payment-terms/settings"),
  });
}

function SettingsBox() {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const settings = useTermsSettings();
  const save = useMutation({
    mutationFn: (s: PaymentTermsSettings) =>
      api("/payment-terms/settings", { method: "PUT", body: JSON.stringify(s) }),
    onSuccess: () => {
      toast("success", "Configuração salva");
      qc.invalidateQueries({ queryKey: ["payment-terms", companyId, "settings"] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <div className="box card p-3">
      <h2 className="h6">Vencimentos</h2>
      <QueryStatus loading={settings.isLoading} error={settings.error} />
      {settings.isSuccess && (
        <div className="form-check">
          <input
            id="postpone-business-day"
            type="checkbox"
            className="form-check-input"
            checked={settings.data.postponeToBusinessDay}
            disabled={!can(me.permissions as Permissions, "payment_terms", "update") || save.isPending}
            onChange={(e) => save.mutate({ postponeToBusinessDay: e.target.checked })}
          />
          <label className="form-check-label" htmlFor="postpone-business-day">
            Prorrogar vencimentos para o próximo dia útil
          </label>
          <div className="form-text">
            Parcela que vence em sábado, domingo ou feriado nacional passa para o próximo dia útil.
          </div>
        </div>
      )}
    </div>
  );
}

/** Lê o texto dos prazos; devolve os dias ou a mensagem de erro. */
function readDays(text: string): { days: number[] } | { error: string } {
  try {
    return { days: parseTermDays(text) };
  } catch (e) {
    return { error: (e as Error).message };
  }
}

function TermDialog({
  id,
  initial,
  onClose,
  onSaved,
}: {
  id?: string;
  initial: PaymentTermInput;
  onClose(): void;
  onSaved(): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const settings = useTermsSettings();
  const [open, setOpen] = useState(true);
  const [name, setName] = useState(initial.name);
  const [daysText, setDaysText] = useState(initial.days.join(" "));
  const [downPayment, setDownPayment] = useState(initial.downPayment);
  const [active, setActive] = useState(initial.active);
  const [total, setTotal] = useState<number | null>(100_000);
  const [baseDate, setBaseDate] = useState(todayInSaoPaulo());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const parsed = readDays(daysText);
  const term = "days" in parsed ? { days: parsed.days, downPayment } : null;
  const daysError = "error" in parsed ? parsed.error : term && termProblem(term);
  const preview =
    term && !daysError && total !== null && total >= 0 && isIsoDate(baseDate)
      ? buildInstallments(total, term, baseDate, {
          postponeToBusinessDay: settings.data?.postponeToBusinessDay ?? false,
        })
      : null;
  const save = useMutation({
    mutationFn: () =>
      api(id ? `/payment-terms/${id}` : "/payment-terms", {
        method: id ? "PUT" : "POST",
        body: JSON.stringify({ name, days: term?.days ?? [], downPayment, active }),
      }),
    onSuccess: () => {
      toast("success", "Condição de pagamento salva");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else if (e instanceof ApiFailure && e.code === "duplicate_payment_term") setErrors({ name: e.message });
      toast("danger", errorMessage(e));
    },
  });
  return (
    <Modal
      open={open}
      title={id ? "Editar condição de pagamento" : "Nova condição de pagamento"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending || !!daysError}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="Nome" required className="col-12" error={errors.name}>
          <input
            value={name}
            maxLength={60}
            placeholder={term && !daysError ? describeTerm(term) : "Ex.: 30/60/90"}
            data-autofocus
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field
          label="Prazos (dias)"
          required
          className="col-md-8"
          error={daysError ?? errors.days}
          hint="Dias a partir da data base, separados por espaço ou barra. Ex.: 30/60/90; 0 = à vista."
        >
          <input value={daysText} inputMode="numeric" onChange={(e) => setDaysText(e.target.value)} />
        </Field>
      </div>
      <div className="form-check">
        <input
          id="term-down"
          type="checkbox"
          className="form-check-input"
          checked={downPayment}
          onChange={(e) => setDownPayment(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="term-down">
          Entrada à vista (vence na data base)
        </label>
      </div>
      <div className="form-check mb-3">
        <input
          id="term-active"
          type="checkbox"
          className="form-check-input"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="term-active">
          Ativa
        </label>
      </div>
      <h3 className="h6">Prévia das parcelas</h3>
      <div className="row">
        <Field label="Total" className="col-md-6">
          <MoneyInput value={total} onChange={setTotal} />
        </Field>
        <Field label="Data base" className="col-md-6">
          <input type="date" value={baseDate} onChange={(e) => setBaseDate(e.target.value)} />
        </Field>
      </div>
      {preview && (
        <div className="table-responsive">
          <table className="table table-sm mb-0" aria-label="Prévia das parcelas">
            <thead>
              <tr>
                <th scope="col">Parcela</th>
                <th scope="col">Vencimento</th>
                <th scope="col" className="text-end">
                  Valor
                </th>
              </tr>
            </thead>
            <tbody>
              {preview.map((p) => (
                <tr key={p.number}>
                  <td>
                    {p.number}/{preview.length}
                  </td>
                  <td>{formatDateBR(p.dueDate)}</td>
                  <td className="text-end">{formatBRL(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
